import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Empty, Modal, Table, Tag } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import { collect } from 'collect.js';
import Document from '../pdf/Document';
import useHelpers from '../../hooks/context/useHelpers';

export default function SignatureHistory({ history, className }) {
  const { t } = useTranslation(undefined, { keyPrefix: 'Components:Contract:SignatureHistory' });
  const { formatDateTime } = useHelpers();

  const [previewEntry, setPreviewEntry] = useState(null);

  const entries = useMemo(
    () =>
      collect(Object.values(history ?? {}))
        .filter(Boolean)
        .sortByDesc((entry) => entry.version)
        .all(),
    [history],
  );

  const columns = [
    {
      title: t('columns.version'),
      dataIndex: 'version',
      key: 'version',
      render: (version) => <Tag color="blue">{t('labels.version', { version })}</Tag>,
    },
    {
      title: t('columns.signed_at'),
      dataIndex: 'signed_at',
      key: 'signed_at',
      render: (signedAt) => (signedAt ? formatDateTime(signedAt) : '—'),
    },
    {
      title: t('columns.internal_note'),
      dataIndex: 'internal_note',
      key: 'internal_note',
      render: (note) => note || '—',
    },
    {
      title: t('columns.signature_provider'),
      dataIndex: 'signature_provider',
      key: 'signature_provider',
      render: (provider) =>
        provider ? (
          <Tag color={provider === 'yousign' ? 'green' : 'default'}>{t(`labels.providers.${provider}`, provider)}</Tag>
        ) : (
          '—'
        ),
    },
    {
      title: t('columns.file'),
      key: 'file',
      align: 'right',
      render: (_, entry) =>
        entry.file_url ? (
          <button
            type="button"
            title={t('buttons.preview')}
            onClick={() => setPreviewEntry(entry)}
            className="ml-auto block h-[88px] w-[64px] overflow-hidden rounded border border-gray-200 transition-shadow hover:shadow-md"
          >
            <div className="pointer-events-none flex h-full w-full items-start justify-center">
              <Document
                file={entry.file_url}
                defaultScale={0.12}
                hideControls
                placeholder={<div className="h-full w-full bg-gray-50" />}
              />
            </div>
          </button>
        ) : (
          '—'
        ),
    },
    {
      title: t('columns.actions'),
      key: 'actions',
      align: 'right',
      render: (_, entry) =>
        entry.file_url ? (
          <Button
            title={t('buttons.download')}
            icon={<DownloadOutlined />}
            onClick={() => window.open(entry.file_url, '_blank')}
          />
        ) : (
          '—'
        ),
    },
  ];

  if (entries.length === 0) {
    return <Empty className={className} description={t('labels.no_history')} />;
  }

  return (
    <>
      <Table
        className={className}
        rowKey={(entry) => entry.version}
        columns={columns}
        dataSource={entries}
        pagination={false}
      />
      {previewEntry && (
        <Modal
          open
          centered
          title={t('labels.version', { version: previewEntry.version })}
          onCancel={() => setPreviewEntry(null)}
          footer={null}
          className="!min-w-[max(600px,50%)]"
        >
          <Document file={previewEntry.file_url} className="mx-auto" />
        </Modal>
      )}
    </>
  );
}
